import {
    collection,
    doc,
    setDoc,
    updateDoc,
    getDocs,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    Timestamp,
    writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { SavedSearch, ViewMode } from '../types';
import { safeErrorMessage } from '../utils/sanitize';
import type { StateSlug } from '../lib/stateCore.mjs';

/**
 * Saved searches are stored as subcollections under the user:
 * savedSearches/{userId}/items/{searchId}
 */

const MAX_SAVED_SEARCHES = 10;

/**
 * Get the saved searches subcollection reference for a user
 */
const getUserSavedSearchesRef = (userId: string) => {
    return collection(db, 'savedSearches', userId, 'items');
};

/**
 * Save a new search
 */
export const saveSearch = async (
    userId: string,
    name: string,
    location: string,
    radius: number,
    useRadius: boolean,
    filters: { styles: string[]; ageGroups: string[]; experienceLevels: string[] },
    viewMode: ViewMode,
    // UI-01 hardening (review fix): the state the search is being saved FROM
    // (the saving page's route state) — REQUIRED, and placed before the optional
    // params so TypeScript enforces it. A defaulted param here would let a future
    // call site silently write a mislabelled search; null-for-legacy semantics
    // belong to the READ side only (getSavedSearches maps absent → null).
    state: StateSlug,
    isDefault: boolean = false,
    locationData?: {
        location_entry_id?: string | null;
        location_source?: "index" | "legacy" | null;
    }
): Promise<SavedSearch> => {
    try {
        // Check if user already has max saved searches
        const existing = await getSavedSearches(userId);
        if (existing.length >= MAX_SAVED_SEARCHES) {
            throw new Error(`Maximum of ${MAX_SAVED_SEARCHES} saved searches reached`);
        }

        // Use timestamp as search ID
        const searchId = `${Date.now()}`;
        const searchRef = doc(db, 'savedSearches', userId, 'items', searchId);

        // If setting as default, unset other defaults first
        if (isDefault) {
            await unsetAllDefaults(userId);
        }

        const now = new Date().toISOString();
        const searchData = {
            name,
            location,
            // UI-01: required at this layer, so new documents always carry a
            // real slug; null exists only on documents saved before stamping.
            state,
            radius,
            useRadius,
            filters,
            viewMode,
            isDefault,
            location_entry_id: locationData?.location_entry_id || null,
            location_source: locationData?.location_source || null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        await setDoc(searchRef, searchData);

        return {
            id: searchId,
            userId,
            ...searchData,
            createdAt: now,
            updatedAt: now
        };
    } catch (error) {
        console.error('[savedSearchesService] Failed to save search:', safeErrorMessage(error));
        if (error instanceof Error && error.message.includes('Maximum')) {
            throw error;
        }
        throw new Error('Failed to save search. Please try again.');
    }
};

/**
 * Get all saved searches for a user
 */
export const getSavedSearches = async (userId: string): Promise<SavedSearch[]> => {
    const searchesRef = getUserSavedSearchesRef(userId);
    const q = query(searchesRef, orderBy('createdAt', 'desc'));

    const snapshot = await getDocs(q);

    return snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            userId,
            name: data.name,
            location: data.location,
            // UI-01: legacy docs have no state field; null and absent both mean
            // "apply against DEFAULT_STATE" at the read sites.
            state: data.state ?? null,
            radius: data.radius,
            useRadius: data.useRadius ?? false,
            filters: data.filters,
            viewMode: data.viewMode,
            isDefault: data.isDefault || false,
            location_entry_id: data.location_entry_id || null,
            location_source: data.location_source || null,
            createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate().toISOString()
                : new Date().toISOString(),
            updatedAt: data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate().toISOString()
                : new Date().toISOString()
        };
    });
};

/**
 * Update a saved search (rename or modify)
 * Note: Now requires userId since subcollection path needs it
 */
export const updateSavedSearch = async (
    userId: string,
    searchId: string,
    updates: Partial<Pick<SavedSearch, 'name' | 'isDefault'>>
): Promise<void> => {
    const searchRef = doc(db, 'savedSearches', userId, 'items', searchId);

    // If setting as default, unset other defaults first
    if (updates.isDefault) {
        await unsetAllDefaults(userId);
    }

    // updateDoc (not setDoc merge) so a missing/deleted doc throws not-found
    // rather than silently upserting a name-only ghost; also drops a pre-read RTT.
    try {
        await updateDoc(searchRef, {
            ...updates,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        if ((error as { code?: string })?.code === 'not-found') {
            throw new Error('Saved search not found');
        }
        throw error;
    }
};

/**
 * Delete a saved search
 * Note: Now requires userId since subcollection path needs it
 */
export const deleteSavedSearch = async (userId: string, searchId: string): Promise<void> => {
    try {
        const searchRef = doc(db, 'savedSearches', userId, 'items', searchId);
        await deleteDoc(searchRef);
    } catch (error) {
        console.error('[savedSearchesService] Failed to delete saved search:', safeErrorMessage(error));
        throw new Error('Failed to delete search. Please try again.');
    }
};

/**
 * Clear all saved searches for a user
 */
export const clearSavedSearches = async (userId: string): Promise<void> => {
    const searchesRef = getUserSavedSearchesRef(userId);
    const snapshot = await getDocs(searchesRef);

    if (!snapshot.empty) {
        const batch = writeBatch(db);
        snapshot.docs.forEach((docSnap) => {
            batch.delete(docSnap.ref);
        });
        await batch.commit();
    }
};

/**
 * Helper: Unset all default flags for a user
 */
const unsetAllDefaults = async (userId: string): Promise<void> => {
    const searchesRef = getUserSavedSearchesRef(userId);
    const q = query(searchesRef, where('isDefault', '==', true));

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
        const batch = writeBatch(db);
        snapshot.docs.forEach((docSnap) => {
            batch.update(docSnap.ref, { isDefault: false, updatedAt: serverTimestamp() });
        });
        await batch.commit();
    }
};
